import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import DtoQualitometer from '../../../quality/dto/DtoQualitometer'
import { getDate } from '../../../utils/DateUtil'
import { groupBy, isNil, isUndefined, keys, maxBy, orderBy } from 'lodash'
import SupportAction from '../../../referencial/components/support/actions/SupportAction'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Icon, IconButton } from '@mui/material'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import Select from 'components/forms/Select'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import { CardTable } from 'components/datatable/NewTable'
import { disabledColor, greyBlue } from 'utils/constants/ColorTheme'
import { getExport } from 'utils/linkUtils'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import useBoolean from 'utils/customHook/useBoolean'
import Fancybox from 'components/fancybox/Fancybox'
import { getFileNatureLabel, isFileOfSamplePoint } from 'utils/FileUtils'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import { checkMandatoryFields } from 'utils/FormUtils'
import { genericPromise2 } from 'utils/ActionUtils'
import ApplicationConf from 'conf/ApplicationConf'
import ToastrAction from 'toastr/actions/ToastrAction'
import QualityAction from 'quality/actions/QualityAction'
import { LINK_STATION_TYPES, STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import StationAction from '../../actions/StationAction'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { filterActiveElements } from 'utils/ListUtil'
import { isAuthorized } from '../../../utils/HabilitationUtil'
import { H_STATION_QUALITO_OPERATIONS } from '../../../account/constants/AccessRulesConstants'

const headers = ['support', 'startDate', 'endDate', 'x', 'y', 'projection', 'id', 'name', 'code', 'startDepth', 'endDepth', 'nbOperations']
const headersWithoutDepths = headers.filter(h => h !== 'startDepth' && h !== 'endDepth')
const mandatoryFields = [{ field: 'startDate' }, { field: 'name' }, { field: 'codeSupport', i18n: 'support' }]

const usePhysicoChemistryAccess = () => useSelector(store => isAuthorized(H_STATION_QUALITO_OPERATIONS, store))

const pointLabel = sp => `${sp.name ?? ''} [${sp.identifiant ?? sp.code ?? sp.idPoint}]`.trim()

const SamplePointCard = ({
    samplePoint = {},
    onChange = () => { },
    disabled = false,
    readMode = false,
    hasPCOperations = true,
}) => {
    const {
        supports,
        pictures,
    } = useSelector(store => ({
        supports: store.SupportReducer.supports,
        pictures: store.StationReducer.pictures,
    }), shallowEqual)

    const projectionList = useSandreList(SANDRE.PROJECTION)
    const fileNatures = useSandreList(SANDRE.TYPE_PHOTO)

    const hasPCAccess = usePhysicoChemistryAccess()
    const displayPCFields = hasPCOperations && hasPCAccess

    const filteredPictures = pictures.filter(p => isFileOfSamplePoint(p, samplePoint))
    const groupedPictures = groupBy(orderBy(filteredPictures, 'date'), p => getFileNatureLabel(p, fileNatures))

    const activeSupports = filterActiveElements(supports)

    return (
        <Card>
            <CardContent>
                <Grid container columnSpacing={1}>
                    <Grid size={6}>
                        <SimpleDatePicker
                            value={samplePoint.startDate}
                            label={i18n.sampleDate}
                            onChange={v => onChange({ startDate: v })}
                            disabled={disabled}
                            readMode={readMode}
                            obligatory
                        />
                    </Grid>
                    <Grid size={6}>
                        <SimpleDatePicker
                            value={samplePoint.endDate}
                            label={i18n.endDate}
                            onChange={v => onChange({ endDate: v })}
                            disabled={disabled}
                            readMode={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={i18n.id}
                            value={samplePoint.identifiant}
                            onChange={v => onChange({ identifiant: v })}
                            disabled={disabled}
                            readMode={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={i18n.code}
                            value={samplePoint.code}
                            onChange={v => onChange({ code: v })}
                            disabled={disabled}
                            readMode={readMode}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.type}
                            options={LINK_STATION_TYPES}
                            value={samplePoint.pointType}
                            onChange={v => onChange({ pointType: v })}
                            disabled={disabled}
                            readMode={readMode}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Input
                            title={i18n.name}
                            value={samplePoint.name}
                            onChange={v => onChange({ name: v })}
                            disabled={disabled}
                            readMode={readMode}
                            obligatory
                        />
                    </Grid>
                    <Grid size={3}>
                        <NumberField
                            value={samplePoint.x}
                            title={'X'}
                            onChange={v => onChange({ x: v })}
                            disabled={disabled}
                            readMode={readMode}
                            floatValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <NumberField
                            value={samplePoint.y}
                            title={'Y'}
                            onChange={v => onChange({ y: v })}
                            disabled={disabled}
                            readMode={readMode}
                            floatValue
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            options={projectionList}
                            label={i18n.projection}
                            value={samplePoint.projection}
                            onChange={v => onChange({ projection: v })}
                            disabled={disabled}
                            readMode={readMode}
                        />
                    </Grid>
                    <Grid size={3}>
                        <NumberField
                            value={samplePoint.xUpstream}
                            title={i18n.xUpstream}
                            onChange={v => onChange({ xUpstream: v })}
                            disabled={disabled}
                            readMode={readMode}
                            floatValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <NumberField
                            value={samplePoint.yUpstream}
                            title={i18n.yUpstream}
                            onChange={v => onChange({ yUpstream: v })}
                            disabled={disabled}
                            readMode={readMode}
                            floatValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <NumberField
                            value={samplePoint.xDownstream}
                            title={i18n.xDownstream}
                            onChange={v => onChange({ xDownstream: v })}
                            disabled={disabled}
                            readMode={readMode}
                            floatValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <NumberField
                            value={samplePoint.yDownstream}
                            title={i18n.yDownstream}
                            onChange={v => onChange({ yDownstream: v })}
                            disabled={disabled}
                            readMode={readMode}
                            floatValue
                        />
                    </Grid>
                    <Grid size={displayPCFields ? 3 : 6}>
                        <SuperMultiAutocomplete
                            options={activeSupports}
                            onChange={(value) => onChange({ codeSupport: !isUndefined(value) ? parseInt(value) : undefined })}
                            keyValue='id'
                            label={i18n.support}
                            values={`${samplePoint.codeSupport}`}
                            disabled={disabled || !isNil(samplePoint?.idPoint)}
                            readMode={readMode}
                            obligatory
                        />
                    </Grid>
                    {displayPCFields && (
                        <>
                            <Grid size={3}>
                                <NumberField
                                    value={samplePoint.startDepth}
                                    title={i18n.startDepth}
                                    onChange={v => onChange({ startDepth: v })}
                                    disabled={disabled}
                                    readMode={readMode}
                                    floatValue
                                />
                            </Grid>
                            <Grid size={3}>
                                <NumberField
                                    value={samplePoint.endDepth}
                                    title={i18n.endDepth}
                                    onChange={v => onChange({ endDepth: v })}
                                    disabled={disabled}
                                    readMode={readMode}
                                    floatValue
                                />
                            </Grid>
                            <Grid size={3}>
                                <NumberField
                                    value={samplePoint.advisedDepth}
                                    title={i18n.advisedDepth}
                                    onChange={v => onChange({ advisedDepth: v })}
                                    disabled={disabled}
                                    readMode={readMode}
                                    floatValue
                                />
                            </Grid>
                        </>
                    )}
                    <Grid size={3}>
                        <NumberField
                            value={samplePoint.totalLength}
                            title={i18n.totalLength}
                            onChange={v => onChange({ totalLength: v })}
                            disabled={disabled}
                            readMode={readMode}
                            floatValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <NumberField
                            value={samplePoint.flowWidth}
                            title={i18n.flowWidth}
                            onChange={v => onChange({ flowWidth: v })}
                            disabled={disabled}
                            readMode={readMode}
                            floatValue
                        />
                    </Grid>
                    {displayPCFields && (
                        <Grid size={6}>
                            <Input
                                title={i18n.mainObject}
                                value={samplePoint.mainObject}
                                onChange={v => onChange({ mainObject: v })}
                                disabled={disabled}
                                readMode={readMode}
                            />
                        </Grid>
                    )}
                    <Grid size={6}>
                        <SimpleTextArea
                            title={i18n.comment}
                            value={samplePoint.comment}
                            onChange={v => onChange({ comment: v })}
                            disabled={disabled}
                            readMode={readMode}
                        />
                    </Grid>
                    <Grid size={6}>
                        <SimpleTextArea
                            title={i18n.advise}
                            value={samplePoint.advise}
                            onChange={v => onChange({ advise: v })}
                            disabled={disabled}
                            readMode={readMode}
                        />
                    </Grid>
                    <Grid size={12} sx={{ overflowX: 'auto', display: 'flex' }}>
                        {keys(groupedPictures).map(labelGroup => (
                            <StyledFieldSet key={labelGroup} style={{ margin: '0 5' }}>
                                <StyledLegend>{labelGroup}</StyledLegend>
                                <div style={{ display: 'flex', alignItems: 'top' }}>
                                    {groupedPictures[labelGroup].map(picture => (
                                        <Fancybox key={picture.name}>
                                            <a href={picture.url} target='_blank' data-fancybox='images' data-caption={picture.name}>
                                                <img
                                                    src={picture.url}
                                                    style={{ width: '150px', height: '180px', objectFit: 'cover', borderRadius: '4px', margin: '0 5' }}
                                                />
                                            </a>
                                        </Fancybox>
                                    ))}
                                </div>
                            </StyledFieldSet>
                        ))}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

SamplePointCard.propTypes = {
    samplePoint: PropTypes.shape({}),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    readMode: PropTypes.bool,
    hasPCOperations: PropTypes.bool,
}

const SamplePointDialog = ({
    isOpen = false,
    defaultSamplePoint,
    hasPCOperations = true,
    onClose = () => { },
    onValidate = () => { },
}) => {
    const [samplePoint, setSamplePoint] = useState(defaultSamplePoint)

    useEffect(() => {
        if (!isOpen) return
        setSamplePoint(defaultSamplePoint)
    }, [isOpen])

    const onChange = (changes) => setSamplePoint(prev => ({ ...prev, ...changes }))

    return (
        <Dialog
            maxWidth='lg'
            open={isOpen}
        >
            <DefaultDialogTitle title={isNil(defaultSamplePoint?.idPoint) ? i18n.newSamplePoint : i18n.pointPrelevement} onClose={onClose} />
            <DialogContent>
                <SamplePointCard
                    samplePoint={samplePoint}
                    onChange={onChange}
                    hasPCOperations={hasPCOperations}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        checkMandatoryFields(mandatoryFields, samplePoint, () => {
                            onValidate(samplePoint)
                            onClose()
                        })
                    }}
                    variant='contained'
                >
                    {i18n.register}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

SamplePointDialog.propTypes = {
    isOpen: PropTypes.bool,
    defaultSamplePoint: PropTypes.shape({
        idPoint: PropTypes.number,
        // other from link
    }),
    hasPCOperations: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

const SamplePointInfoDialog = ({
    isOpen = false,
    onClose = () => { },
    hasPCOperations = true,
    samplePoint = {},
}) => {
    return (
        <Dialog
            maxWidth='lg'
            open={isOpen}
            onClose={onClose}
        >
            <DefaultDialogTitle title={i18n.samplePoint} onClose={onClose} />
            <DialogContent>
                <SamplePointCard
                    samplePoint={samplePoint}
                    hasPCOperations={hasPCOperations}
                    disabled
                />
            </DialogContent>
        </Dialog>
    )
}

SamplePointInfoDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    hasPCOperations: PropTypes.bool,
    samplePoint: PropTypes.shape({}),
}

const SamplePointIconInfo = ({
    samplePoint,
    disabled = false,
    hasPCOperations = true,
}) => {
    const {
        value: isOpen,
        setTrue: onOpen,
        setFalse: onClose,
    } = useBoolean(false)

    const isDisabled = disabled || isNil(samplePoint)

    return (
        <>
            <IconButton
                sx={{ fontSize: '24px' }}
                onClick={onOpen}
                disabled={isDisabled}
            >
                <Icon fontSize='inherit' sx={{ color: isDisabled ? '#9e9e9e' : '#000' }}>info</Icon>
            </IconButton>
            <SamplePointInfoDialog
                isOpen={isOpen}
                onClose={onClose}
                samplePoint={samplePoint}
                hasPCOperations={hasPCOperations}
            />
        </>
    )
}

SamplePointIconInfo.propTypes = {
    samplePoint: PropTypes.shape({}),
    disabled: PropTypes.bool,
    hasPCOperations: PropTypes.bool,
}

const MERGE_HEADERS = ['select', 'point', 'nbOperations']

const MergeSamplePointDialog = ({
    isOpen = false,
    sourcePoint,
    samplePoints = [],
    operationCounts = {},
    onClose = () => { },
    onValidate = () => { },
}) => {
    const [selectedTarget, setSelectedTarget] = useState()

    useEffect(() => {
        if (!isOpen) return
        setSelectedTarget(undefined)
    }, [isOpen])

    if (isNil(sourcePoint)) return null

    const supportOf = p => parseInt(p.codeSupport ?? 3) // support vide = 3 [Eau]
    const otherPoints = samplePoints.filter(p => p.idPoint !== sourcePoint.idPoint && supportOf(p) === supportOf(sourcePoint))
    const sourceCount = operationCounts[sourcePoint.idPoint] ?? 0
    const targetCount = !isNil(selectedTarget) ? (operationCounts[selectedTarget.idPoint] ?? 0) : 0

    const rows = otherPoints.map(p => {
        const isSelected = selectedTarget?.idPoint === p.idPoint
        return {
            select: (
                <Icon sx={{ color: isSelected ? greyBlue : disabledColor }}>
                    {isSelected ? 'radio_button_checked' : 'radio_button_unchecked'}
                </Icon>
            ),
            point: pointLabel(p),
            nbOperations: operationCounts[p.idPoint] ?? 0,
            idPoint: p.idPoint,
        }
    })

    return (
        <Dialog maxWidth='md' fullWidth open={isOpen} onClose={onClose}>
            <DefaultDialogTitle title={i18n.mergeSamplePoints} onClose={onClose} />
            <DialogContent>
                <CardTable
                    title={i18n.samplePoint}
                    rows={rows}
                    headers={MERGE_HEADERS}
                    color={greyBlue}
                    onClickRow={row => setSelectedTarget(otherPoints.find(p => p.idPoint === row.idPoint))}
                    rowsPerPageOptions={[{ value: 1000, label: '1000' }]}
                />
                {!isNil(selectedTarget) && (
                    <StyledFieldSet style={{ marginTop: '10px' }}>
                        <StyledLegend>{i18n.summary}</StyledLegend>
                        {`${pointLabel(sourcePoint)} (${sourceCount} ${i18n.operations}) => ${pointLabel(selectedTarget)} (${targetCount} ${i18n.operations})`}
                    </StyledFieldSet>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant='contained'
                    disabled={isNil(selectedTarget)}
                    onClick={() => {
                        onValidate(selectedTarget)
                        onClose()
                    }}
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

MergeSamplePointDialog.propTypes = {
    isOpen: PropTypes.bool,
    sourcePoint: PropTypes.shape({ idPoint: PropTypes.number }),
    samplePoints: PropTypes.arrayOf(PropTypes.shape({})),
    operationCounts: PropTypes.objectOf(PropTypes.number),
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

const QualitySamplePointsPanel = ({
    station = {},
    readMode = false,
    onChange = () => { },
    hasPCOperations = true,
}) => {
    const dispatch = useDispatch()
    const {
        supportsIndex,
        supports,
    } = useSelector(store => ({
        supportsIndex: store.SupportReducer.supportsIndex,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const hasPCAccess = usePhysicoChemistryAccess()

    const {
        value: isOpen,
        setFalse: onClose,
        setTrue: onOpen,
    } = useBoolean(false)
    const {
        value: isMergeOpen,
        setFalse: onCloseMerge,
        setTrue: onOpenMerge,
    } = useBoolean(false)
    const [selectedPoint, setSelectedPoint] = useState()
    const [mergeSource, setMergeSource] = useState()
    const [operationCounts, setOperationCounts] = useState({})

    const loadCounts = () => QualityAction.promiseSamplePointsOperationCount(station.id)
        .then((res = []) => setOperationCounts(Object.fromEntries(res.map(r => [r.idPoint, r.count]))))

    useEffect(() => {
        if (supports.length === 0) {
            dispatch(SupportAction.fetchSupports())
        }
    }, [])

    useEffect(() => {
        if (!isNil(station.id)) {
            loadCounts()
        }
    }, [station.id])

    const onValidateMerge = (target) => {
        QualityAction.promiseMergeSamplePoints(station.id, mergeSource.idPoint, target.idPoint)
            .then(({ targetStartDate } = {}) => {
                onChange({ link_samplePoints: station.link_samplePoints
                    .filter(l => l.idPoint !== mergeSource.idPoint)
                    .map(l => l.idPoint === target.idPoint ? { ...l, startDate: targetStartDate ?? l.startDate } : l) })
                loadCounts()
                dispatch(StationAction.fetchPictures(station.code, STATION_TYPE_CONSTANT.QUALITY))
                dispatch(StationAction.fetchFiles(station.code, STATION_TYPE_CONSTANT.QUALITY))
                dispatch(ToastrAction.success(i18n.mergeSuccess))
            })
            .catch(() => dispatch(ToastrAction.error(i18n.error)))
    }

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const tableData = station.link_samplePoints.map(sp => ({
        support: showSandreCode ? supportsIndex[sp.codeSupport]?.labelWithCode : supportsIndex[sp.codeSupport]?.name,
        startDate: { value: getDate(sp.startDate), sortValue: sp.startDate },
        endDate: getDate(sp.endDate),
        x: sp.x,
        y: sp.y,
        projection: sp.projection,
        id: sp.identifiant,
        name: sp.name,
        code: sp.code,
        startDepth: sp.startDepth,
        endDepth: sp.endDepth,
        nbOperations: operationCounts[sp.idPoint] ?? 0,
        idPoint: sp.idPoint,
    }))

    const displayedHeaders = hasPCAccess ? headers : headersWithoutDepths

    const exportAction = getExport(tableData, i18n.samplePoint, displayedHeaders, null, null, 'white')
    const actions = !readMode ? [{
        icon: 'note_add',
        tooltip: i18n.add,
        color: 'white',
        onClick: () => {
            setSelectedPoint({})
            onOpen()
        },
    }, exportAction] : [exportAction]

    const lineActions = [{
        icon: 'close',
        onClick: (element) => {
            genericPromise2(ApplicationConf.station.pointPrelDataExists(), { method: 'POST', body: { stationType: station.typeName, idStation: station.id, codepoint: element.idPoint } })
                .then(res => {
                    if (!res.exists) {
                        onChange({ link_samplePoints: station.link_samplePoints.filter(l => l.idPoint !== element.idPoint) })
                    } else {
                        dispatch(ToastrAction.error('Ce lieu de prélèvement est utilisé sur une ou plusieurs opérations. Pour pouvoir le supprimer, veuillez modifier ou supprimer les opérations qui le concernent.'))
                    }
                })
        },
        displayed: !readMode,
    }, {
        icon: 'edit',
        onClick: point => {
            setSelectedPoint(station.link_samplePoints.find(l => point.idPoint === l.idPoint))
            onOpen()
        },
        displayed: !readMode,
    }, {
        icon: 'merge',
        tooltip: i18n.merge,
        onClick: point => {
            setMergeSource(station.link_samplePoints.find(l => point.idPoint === l.idPoint))
            onOpenMerge()
        },
        displayed: !readMode,
    }]

    return (
        <>
            <CardTable
                title={i18n.samplePoint}
                rows={tableData}
                headers={displayedHeaders}
                actions={actions}
                color={greyBlue}
                lineActions={lineActions}
            />
            <SamplePointDialog
                isOpen={isOpen}
                defaultSamplePoint={selectedPoint}
                onValidate={newPoint => {
                    if (isNil(selectedPoint.idPoint)) {
                        const idPoint = (maxBy(station.link_samplePoints, 'idPoint')?.idPoint ?? 0) + 1
                        onChange({ link_samplePoints: [...station.link_samplePoints, { ...newPoint, idPoint, idQualitometer: station.id }] })
                        return
                    }
                    const newLinks = station.link_samplePoints.map(point => newPoint.idPoint === point.idPoint ? newPoint : point)
                    onChange({ link_samplePoints: newLinks })
                }}
                onClose={() => {
                    setSelectedPoint()
                    onClose()
                }}
                hasPCOperations={hasPCOperations}
            />
            <MergeSamplePointDialog
                isOpen={isMergeOpen}
                sourcePoint={mergeSource}
                samplePoints={station.link_samplePoints}
                operationCounts={operationCounts}
                onValidate={onValidateMerge}
                onClose={() => {
                    setMergeSource(undefined)
                    onCloseMerge()
                }}
            />
        </>
    )
}

QualitySamplePointsPanel.propTypes = {
    station: PropTypes.instanceOf(DtoQualitometer),
    readMode: PropTypes.bool,
    onChange: PropTypes.func,
    hasPCOperations: PropTypes.bool,
}

export default QualitySamplePointsPanel
export {
    SamplePointIconInfo,
    SamplePointInfoDialog,
    SamplePointCard,
}